import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TokenStorageService } from '../services/token-storage.service';
import { swaggerApiService } from '../services/swaggerApi.service';
import { OAuthClientService } from '../services/oauth-client.service';

@Component({
  selector: 'app-auth-callback',
  template: `
    <div class="callback-container">
      <mat-spinner diameter="48"></mat-spinner>
      <p class="callback-text">{{ statusMessage }}</p>
    </div>
  `,
  styles: [`
    .callback-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 70vh;
      gap: 1.5rem;
    }
    .callback-text {
      font-size: 1.1rem;
      color: #555;
      font-family: inherit;
    }
  `]
})
export class AuthCallbackComponent implements OnInit, OnDestroy {
  statusMessage = 'កំពុងផ្ទៀងផ្ទាត់ការចូលប្រព័ន្ធ...';
  private subs = new Subscription();
  private handled = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tokenStorage: TokenStorageService,
    private swaggerApi: swaggerApiService,
    private oauthClient: OAuthClientService
  ) {}

  ngOnInit(): void {
    if (this.handled) {
      return;
    }
    this.handled = true;

    this.processCallback();
  }

  private processCallback(): void {
    const params = this.route.snapshot.queryParams;
    const token = params['token'] || params['accessToken'] || params['access_token'];
    const refreshToken = params['refreshToken'] || params['refresh_token'];
    const sessionId = params['sessionId'] || params['session_id'];
    const userId = params['userId'] || params['user_id'];
    const code = params['code'];
    const state = params['state'];
    let returnUrl = params['returnUrl'] || params['return_url'] || '/contents/1/km';

    // 1. Direct tokens from query params
    if (token) {
      this.tokenStorage.saveAccessToken(token);
      this.tokenStorage.saveIsUserLogin('true');
      if (sessionId) {
        this.tokenStorage.saveSessionId(sessionId);
      }
      if (userId) {
        this.tokenStorage.saveUserId(userId);
      }
      if (refreshToken) {
        this.tokenStorage.saveRefreshToken(refreshToken);
      }

      this.syncProfileAndFinish(returnUrl);
      return;
    }

    // 2. OAuth PKCE code exchange
    if (code) {
      try {
        this.subs.add(
          this.oauthClient.exchangeCode(code, state).subscribe({
            next: () => {
              const target = returnUrl || this.oauthClient.consumeReturnUrl() || '/contents/1/km';
              this.syncProfileAndFinish(target);
            },
            error: (err) => {
              console.error('OAuth code exchange failed:', err);
              this.finishRedirect(returnUrl);
            }
          })
        );
        return;
      } catch (err) {
        console.error('Error exchanging OAuth code:', err);
        this.finishRedirect(returnUrl);
        return;
      }
    }

    // 3. Existing stored session
    if (this.tokenStorage.hasToken()) {
      this.syncProfileAndFinish(returnUrl);
      return;
    }

    // 4. Missing credentials
    console.warn('Auth callback received without valid token or code');
    this.finishRedirect(returnUrl);
  }

  private syncProfileAndFinish(returnUrl: string): void {
    this.subs.add(
      this.swaggerApi.userProfile().subscribe({
        next: (res) => {
          if (res) {
            const profile = res.user || res.data || res.item || res.profileInfo || res;
            this.tokenStorage.saveUser(profile);
            const img = profile.profile || profile.image || profile.avatar || '';
            if (img) {
              this.tokenStorage.saveProfileImage(img);
            }
            this.tokenStorage.saveIsUserLogin('true');
          }
          this.finishRedirect(returnUrl);
        },
        error: (err) => {
          console.warn('Could not sync user profile in callback:', err);
          this.finishRedirect(returnUrl);
        }
      })
    );
  }

  private finishRedirect(returnUrl: string): void {
    const target = this.sanitizeTarget(returnUrl);
    if (!target) {
      return;
    }
    try {
      this.router.navigateByUrl(target);
    } catch {
      this.router.navigate(['/contents/1/km']);
    }
  }

  private sanitizeTarget(returnUrl: string): string {
    if (!returnUrl) {
      return '/contents/1/km';
    }
    let target = returnUrl.trim();
    if (target.startsWith('http://') || target.startsWith('https://')) {
      try {
        const parsed = new URL(target);
        if (parsed.origin === window.location.origin) {
          target = parsed.pathname + parsed.search + parsed.hash;
        } else {
          window.location.href = target;
          return '';
        }
      } catch {
        return '/contents/1/km';
      }
    }

    if (target.includes('http:') || target.includes('http%3A') || target === '/auth/callback' || !target || target === '/') {
      return '/contents/1/km';
    }

    if (!target.startsWith('/')) {
      target = '/' + target;
    }

    return target;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
